import React, {useMemo} from 'react';
import {
  getProjectPostProcessorTemplate,
  getProjectPostProcessorTemplateUrl,
} from '../../../api/postprocessor/GetNewPostProcessor';
import {useHistory, useLocation, useParams} from 'react-router';
import useSWR from 'swr';
import {updatePostProcessor} from '../../../api/postprocessor/UpdatePostProcessor';
import {toast} from '../../../base-components/StudioToast';
import {useIntl} from 'react-intl';
import {PostProcessingForm} from './PostProcessingForm';
import {UpdatePostProcessorRequest} from '../../../types/postprocessor/UpdatePostProcessorRequest';
import {ApplicationType} from '../../../types/model/framework/ApplicationType';
import {ModelPurpose} from '../../../types/model/ModelPurpose';
import {usePostProcessorList} from '../../../api/postprocessor/GetPostProcessorList';

type ReturnToOptions = 'spaCreate' | undefined;

export const CreatePostProcessor = () => {
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location]);
  const returnTo = search.get('returnTo') as ReturnToOptions;
  const useDefault = search.has('useDefault')
    ? (search.get('useDefault') as ModelPurpose)
    : undefined;
  const modelPurpose = search.has('modelPurpose')
    ? (search.get('modelPurpose') as ModelPurpose)
    : undefined;
  const appType = search.has('appType')
    ? (search.get('appType') as ApplicationType)
    : undefined;
  const spaId = search.get('spaId');
  const deploymentId = search.get('deploymentId');
  const applicationId = search.get('applicationId');

  const {projectId} = useParams<{projectId: string}>();
  const cacheKey = getProjectPostProcessorTemplateUrl(projectId);
  const {data} = useSWR(cacheKey, () => getProjectPostProcessorTemplate({projectId}), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const {mutate} = usePostProcessorList({projectId});

  const intl = useIntl();
  const history = useHistory();

  const returnToPreviousPage = () => {
    if (returnTo === 'spaCreate' && applicationId) {
      if (deploymentId) {
        history.push(
          `/project/${projectId}/inference/${applicationId}/deployment/${deploymentId}/configure?spaId=${spaId}`
        );
      } else {
        history.push(
          `/project/${projectId}/inference/${applicationId}/create?spaId=${spaId}`
        );
      }
    } else {
      history.push(`/project/${projectId}/upload`);
    }
  };

  const getReturnText = () => {
    switch (returnTo) {
      case 'spaCreate':
        return intl.formatMessage({id: 'form.returnToConfigure'});
      default:
        return undefined;
    }
  };

  const handleSubmit = async (values: UpdatePostProcessorRequest) => {
    try {
      const resp = await updatePostProcessor(values);
      if (!resp.errors?.length) {
        toast.success(intl.formatMessage({id: 'createPostProcessor.success'}));
        mutate();
        returnToPreviousPage();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <PostProcessingForm
      title={intl.formatMessage({id: 'createPostProcessor.header'})}
      returnText={getReturnText()}
      projectId={projectId}
      isInitializingData={!data?.body}
      template={
        applicationId && data?.body
          ? {
              ...data?.body,
              applicationType: appType,
              postProcessorType: 'APPLICATION',
              executionContainer: 'BROWSER',
              useDefaultFile: useDefault,
              modelPurpose: modelPurpose,
            }
          : data?.body
      }
      onSubmit={handleSubmit}
      onCancel={returnToPreviousPage}
    />
  );
};
